<template>
    <div class="exchanges-rate-wrapper" :class="{'mobile': getMobileAppState}">
        <!-- Loader Generic -->
        <loaders-generic v-if="showLoader" isSmall fullSize/>
        <!-- Error on getting exchanges rates info -->
        <infobox-generic v-if="applyErrorState" state="negative" :label="t('home.exchangesError')"/>
        <!-- Arrow Left -->
        <transition name="appear">
			<div v-if="scrollLeft > 0" class="icon-wrapper left">
				<button-quaternary v-if="!getMobileAppState" icon="arrow-left" buttonSize="tiny" class="icon" @clicked="[scrollLeftHandler(), startLoop()]"/>
			</div>
		</transition>
        <div
        v-if="!showLoader && !applyErrorState"
        class="exchanges-rate-scroll-wrapper"
        ref="scrollElementRef"
        v-dragscroll
        v-on:dragscrollstart="grabing = true"
		v-on:dragscrollend="grabing = false"
		:class="{'grabing': grabing}"
        @scroll="scrollLeft = $event.target.scrollLeft">
            <div v-for="rate in parsedRates" :key="`rate${rate}`" class="rate-element">
                <component :is="rate.currencyIcon"/>
                <div class="text-wrapper">
                    <div class="currency">
                        <div class="label">{{rate.currencyLabel}}</div>
                        <div class="description">{{rate.currencyDescription}}</div>
                    </div>
                    <div class="value">{{rate.value}}</div>
                </div>
            </div>
        </div>
        <!-- Arrow Right -->
        <transition name="appear">
			<div v-if="!scrollIsAtEnd" class="icon-wrapper right">
				<button-quaternary v-if="!getMobileAppState" icon="arrow-right" buttonSize="tiny" class="icon" @clicked="[scrollRightHandler(), startLoop()]"/>
			</div>
		</transition>
    </div>
</template>

<script setup>
import LoadersGeneric from '@/components/generic/loaders/Generic'
import InfoboxGeneric from '@/components/generic/infobox/Generic'
import ButtonQuaternary from '@/components/generic/buttons/Quaternary'

/* Vue */
import { ref, computed, defineAsyncComponent, onBeforeUnmount, watch, onMounted } from 'vue'
/* Aux */
import { flagCurrenciesIconMapper } from '@/assets/scripts/mappers/flags'
import { scrollTo } from '@/assets/scripts/scrollAnimation'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* mapActions & mapGetters */
const { requestExchangeTableList } = mapActions(moduleMapper.HOME)
const { getExchangeTableList } = mapGetters(moduleMapper.HOME)
const { getMobileAppState } = mapGetters(moduleMapper.HOME)

/* Props
@showExchangesLoader {Boolean} - prop to tell widget fetch state
@applyExchangesErrorState {Boolean} - prop to tell widget fetch error state
*/
const props = defineProps({
    showExchangesLoader: Boolean,
    applyExchangesErrorState: Boolean
})

/* Data */
const showLoader = ref(props.showExchangesLoader)
const applyErrorState = ref(props.applyExchangesErrorState)

const scrollLeft = ref(0)
const scrollElementRef = ref('')
const grabing = ref(false)

const parsedRates = computed(() => {
    return getExchangeTableList.value.filter(obj => obj.type === 1).map(rate => {
        return {
            currencyLabel: `1 ${rate.currency}`,
            currencyDescription: rate.description,
            currencyIcon: defineAsyncComponent(() => import('@/assets/icons/flags/' + flagCurrenciesIconMapper(rate.currency) + '.svg?inline')),
            value: `Kz ${JSON.stringify(rate.sellValue).replace('.', ',')}`
        }
    })
})

const RATE_ELEMENT_SIZE = getMobileAppState.value ? 148 : 248

const scrollIsAtEnd = computed(() => {
    return scrollElementRef.value.clientWidth + scrollLeft.value === scrollElementRef.value.scrollWidth
})

const scrollLeftHandler = $event => {
    if (!scrollElementRef.value) return
    const valueToMove = $event ? RATE_ELEMENT_SIZE : RATE_ELEMENT_SIZE
    scrollLeft.value = scrollElementRef.value.scrollLeft - valueToMove
    scrollTo(scrollElementRef.value, scrollLeft.value, null, ($event ? 1000 : 1000), 'horizontal')
}

const scrollRightHandler = $event => {
    if (!scrollElementRef.value) return
    const valueToMove = $event ? RATE_ELEMENT_SIZE : RATE_ELEMENT_SIZE
    scrollLeft.value = (scrollElementRef.value.scrollLeft + valueToMove) - (getMobileAppState.value ? 0 : 28)
    scrollTo(scrollElementRef.value, scrollLeft.value, null, ($event ? 1000 : 1000), 'horizontal')
}

/*************************************************
*                                                *
*                  Request Logics                *
*                                                *
*************************************************/
watch(() => props.showExchangesLoader, (newVal, oldVal) => {
    if (!props.showExchangesLoader) {
        showLoader.value = false
		startLoop()
    }
})

watch(() => props.applyExchangesErrorState, (newVal, oldVal) => {
    if (props.applyExchangesErrorState) applyErrorState.value = true
})

const fecthExchangeTableList = async () => {
    showLoader.value = true
	applyErrorState.value = false

    const payload = { currency: 'AKZ', quotationDate: new Date() }

	try {
        await requestExchangeTableList(payload)
        showLoader.value = false
        startLoop()
	} catch (error) {
		if (error.toString() === 'Cancel') return
		applyErrorState.value = true
	}
}

let interval = null
/** CR-12112023 - Percebo a lógica do moving right, mas confirmaste isto com o JC? O que me fazia mais sentido aqui (apesar de dar mais trabalho) era isto ser um anel, ou seja, quando chegas ao fim, começam a entrar à direita as moedas do início, e estás sempre a rodar para o mesmo lado, a primeira entra à direita da última, e assim sucessivamente. Pelo que está no Miro é o que retiro dali */
const movingRight = ref(true)

watch(() => scrollIsAtEnd.value, (newVal, oldVal) => {
    movingRight.value = false
})

watch(() => scrollLeft.value, (newVal, oldVal) => {
    if (scrollLeft.value === 0) movingRight.value = true
})

const startLoop = () => {
    if (interval) clearInterval(interval)

    interval = setInterval(() => {
        if (!movingRight.value) scrollLeftHandler()
        else scrollRightHandler()
    }, 4000)
}

onMounted(() => {
    if (!props.showExchangesLoader) {
        showLoader.value = false
		startLoop()
    }
})

onBeforeUnmount(() => {
    clearInterval(interval)
})
</script>

<style lang="scss" scoped>
.exchanges-rate-wrapper {
    width: 100%;
    height: 56px;
    overflow: hidden;

    background-color: var(--secondary-light-4);
    border-bottom: 1px solid var(--secondary-light-2);
    position: relative;

    .icon-wrapper {
        position: absolute;
        height: 100%;
        top: 0;

        display: flex;
        align-items: center;

        background-color: var(--secondary-light-4);
        z-index: 1;

        &.left {
            left: 0;
            height: 100%;

            &:before {
                content: '';
                width: 32px;
                height: 100%;

                position: absolute;
                top: 0;
                right: -30px;
            }
        }

        &.right {
            right: 0;
            height: 100%;

            &:before {
                content: '';
                width: 32px;
                height: 100%;

                position: absolute;
                top: 0;
                left: -30px;

                background: var(--gradient-right-fade);
            }
        }
    }

    .exchanges-rate-scroll-wrapper {
        width: 100%;
        height: 100%;

        display: flex;
		overflow: hidden;
		cursor: grab;

        &.grabing {
			cursor: grabbing;
			cursor: -webkit-grabbing;
		}

        .rate-element {
            width: 248px;
            height: 100%;
            padding: 16px;

            display: flex;
            flex-shrink: 0;
            align-items: center;
            gap: 8px;

            color: var(--secondary-dark-2);
            position: relative;

            &:after {
                content: '';
                width: 1px;
                height: 40px;

                position: absolute;
                top: 50%;
                right: 0;

                transform: translateY(-50%);
                background-color: var(--secondary-light-2);
            }

            svg {
                flex-shrink: 0;
                pointer-events: none;
            }

            .text-wrapper {
                width: 100%;

                display: flex;
                justify-content: space-between;
                gap: 8px;
                overflow: hidden;
                pointer-events: none;

                .currency {
                    width: 100%;

                    display: flex;
                    flex-direction: column;
                    flex-shrink: 1;
                    overflow: hidden;

                    .label {
                        width: 100%;
                        @include H6-Medium;
                    }

                    .description {
                        width: 100%;
                        @include H8-Regular;

                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                    }
                }

                .value {
                    width: auto;
                    flex-shrink: 0;

                    @include H7-Regular;
                }
            }
        }
    }

    &.mobile {
        height: 80px;

        background-color: var(--secondary-light-3);
        border-radius: 16px 16px 0px 0px;

        .icon-wrapper {
            background-color: var(--secondary-light-3);

            &.left {
                &:before {
                    background: var(--gradient-left-fade);
                }
            }
        }

        .exchanges-rate-scroll-wrapper {
            align-items: center;

            .rate-element {
                width: 148px;
                height: 48px;
                padding: 16px;

                display: flex;
                flex-shrink: 0;
                gap: 8px;

                color: var(--secondary-dark-2);
                position: relative;

                &:after {
                    content: '';
                    width: 1px;
                    height: 40px;

                    position: absolute;
                    top: 50%;
                    right: 0;

                    transform: translateY(-50%);
                    background-color: var(--secondary-light-2);
                }

                .text-wrapper {
                    flex-direction: column;
                    gap: 2px;

                    .currency {
                        width: 100%;

                        display: flex;
                        flex-direction: column;
                        flex-shrink: 1;
                        overflow: hidden;

                        .label {
                            width: 100%;
                            @include H6-Medium;
                        }

                        .description {
                            width: 100%;
                            @include H8-Regular;

                            overflow: hidden;
                            text-overflow: ellipsis;
                            white-space: nowrap;
                        }
                    }

                    .value {
                        width: auto;
                        flex-shrink: 0;

                        @include H7-Regular;
                    }
                }
            }
        }
    }
}
</style>
